import {
    Alert,
    Button,
    Form,
    Headline,
    InputControl,
    Loading,
    NumberInput,
    StyleFunction,
    Surface,
    useAuth,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    useAccountForProductGroupQuery,
    useIntegrationsOfTypeQuery,
    useProductGroupQuery,
    useProductGroupSaveMutation,
    useSaveAccountForProductGroupMutation,
} from "graphql-sdk";
import {
    IIntegration,
    IProductGroup,
    IProductGroupInput,
    schemaProductGroup,
} from "lib";
import { useTranslation } from "locales";
import React, { useCallback, useEffect, useState } from "react";

import { View } from "react-native";
import { useHandleMutationError } from "../../../../hooks/use-handle-mutation-error";
import { RootStackScreenProps } from "../../../../navigation";
import { AdminContainer } from "../../../container";
import { ProductGroupForm } from "../product-group-form";

type ScreenProps = RootStackScreenProps<"PRODUCT_GROUP_EDIT">;

export function ProductGroupEditScreen({ navigation, route }: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.product");

    const groupId = route.params.id;

    const { navigate } = navigation;

    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [productGroupEdit] = useProductGroupSaveMutation();
    const { handleMutationError } = useHandleMutationError();
    const productGroup = useProductGroupQuery({
        variables: { id: groupId },
        fetchPolicy: "no-cache",
    });
    const form = useForm<IProductGroupInput>(schemaProductGroup, null);
    const [{ values }, { setDefaultValues }] = form;

    const { data: economicIntegrationData } = useIntegrationsOfTypeQuery({
        variables: {
            type: "E-CONOMIC",
        },
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (values) {
            // Values are set - so setDefaultValues() has been called. Back off.
            return;
        }

        if (productGroup.loading) {
            return;
        }

        const pg = productGroup.data.productGroup;

        setDefaultValues({
            externalId: pg.externalId || null,
            name: pg.name,
            vatId: pg.vat.id,
            color: pg.color,
            excludeFromDiscounts: pg.excludeFromDiscounts,
            printerId: pg.printer?.id || null,
            isExternal: pg.isExternal,
        });
    }, [productGroup, productGroup.data, setDefaultValues, values]);

    const edit = useCallback(async () => {
        if (!productGroup.data || !productGroup.data.productGroup || !values) {
            return;
        }

        const updateData = {
            externalId: values.externalId,
            name: values.name,
            vatId: values.vatId!,
            color: values.color,
            excludeFromDiscounts: values.excludeFromDiscounts,
            printerId: values.printerId || null,
            isExternal: values.isExternal,
        };

        await handleMutationError(
            async () =>
                await productGroupEdit({
                    variables: {
                        id: productGroup.data!.productGroup!.id,
                        productGroup: updateData,
                    },
                }),
            t("backoffice.product_group.saved", "Product group saved"),
            () => {
                // update default values for form
                setDefaultValues(updateData);

                navigate("PRODUCT_GROUPS");
            }
        );
    }, [
        handleMutationError,
        navigate,
        productGroup.data,
        productGroupEdit,
        setDefaultValues,
        t,
        values,
    ]);

    return (
        <AdminContainer>
            <View style={styles.container}>
                <View style={styles.left}>
                    {productGroup.loading ? (
                        <Surface>
                            <Loading />
                        </Surface>
                    ) : (
                        <ProductGroupForm
                            form={form}
                            onSubmit={edit}
                            submitButton={["common.save", "Save"]}
                        />
                    )}
                </View>
                <View style={styles.right}>
                    {economicIntegrationData &&
                    economicIntegrationData.integrationsOfType.data.length >
                        0 ? (
                        <AccountForProductGroup
                            integrationId={
                                economicIntegrationData.integrationsOfType
                                    .data[0].id
                            }
                            groupId={groupId}
                        />
                    ) : null}
                </View>
            </View>
        </AdminContainer>
    );
}

type AccountNumberForm = { accountNo: number | null };

const minValue = 0,
    maxValue = 100000;

const accountNumberValidation = {
    accountNo: {
        number: true,
        wholeNumber: true,
        minValue: { value: minValue },
        maxValue: { value: maxValue },
    },
} as const;

function AccountForProductGroup({
    integrationId,
    groupId,
}: {
    integrationId: IIntegration["id"];
    groupId: IProductGroup["id"];
}) {
    const [t] = useTranslation();
    const { handleMutationError } = useHandleMutationError();
    const [saveAccountMutation] = useSaveAccountForProductGroupMutation();
    const {
        data: accountNoData,
        loading: accountNoLoading,
        error: accountNoError,
        refetch,
    } = useAccountForProductGroupQuery({
        variables: {
            integrationId: integrationId,
            productGroupId: groupId,
        },
    });

    const form = useForm<AccountNumberForm>(accountNumberValidation, null);
    const [{ values }, { setDefaultValues }] = form;

    const saveAccountNo = useCallback(async () => {
        if (!accountNoData?.accountForProductGroup || !values) {
            return;
        }

        await handleMutationError(
            async () =>
                await saveAccountMutation({
                    variables: {
                        integrationId: integrationId,
                        productGroupMap: {
                            accountNo: values.accountNo,
                            productGroupId: groupId,
                        },
                    },
                }),
            t("backoffice.product_group.saved", "Product group saved"),
            () => {
                refetch();
            }
        );
    }, [
        accountNoData?.accountForProductGroup,
        groupId,
        handleMutationError,
        integrationId,
        refetch,
        saveAccountMutation,
        t,
        values,
    ]);

    useEffect(() => {
        if (accountNoLoading || !accountNoData) {
            return;
        }

        setDefaultValues({
            accountNo: accountNoData.accountForProductGroup.accountNo,
        });
    }, [accountNoData, accountNoLoading, setDefaultValues]);

    return (
        <Surface>
            <View>
                <>
                    <Headline size="h4" numberOfLines={2}>
                        {t(
                            "backoffice.integration.economic.product_group_account_no",
                            "E-conomic account number"
                        )}
                    </Headline>
                    {accountNoLoading || !values || values.accountNo < 0 ? (
                        <Loading />
                    ) : accountNoError ? (
                        <Alert type="error">
                            {t("backoffice.common.error", "An error occurred.")}
                        </Alert>
                    ) : accountNoData ? (
                        <AccountForProductGroupForm
                            form={form}
                            onSubmit={saveAccountNo}
                        />
                    ) : null}
                </>
            </View>
        </Surface>
    );
}

function AccountForProductGroupForm({
    form,
    onSubmit,
}: {
    form: Form<AccountNumberForm>;
    onSubmit: () => void;
}) {
    const [t] = useTranslation();
    const [invalidInput, setInvalidInput] = useState<boolean>(false);
    const [{ values, errors }, { setValue, handleSubmit }] = form;

    return (
        <View>
            <InputControl
                error={errors.accountNo}
                description={t(
                    "backoffice.integration.economic.account_number_description",
                    "The value here should match an account number with your e-conomic Chart of Accounts or be empty."
                )}
            >
                <NumberInput
                    label={t("common.account_number", "Account number")}
                    value={values.accountNo}
                    min={minValue}
                    max={maxValue}
                    onChange={newValue => {
                        setValue("accountNo", newValue);
                        setInvalidInput(false);
                    }}
                    onInvalid={() => {
                        setInvalidInput(true);
                    }}
                />
            </InputControl>
            <Button onPress={handleSubmit(onSubmit)} disabled={invalidInput}>
                {t("common.save", "Save")}
            </Button>
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        flexDirection: "row",
        flexWrap: "wrap",
    },
    left: {
        marginBottom: theme.spacingScale * 2,
        marginRight: theme.spacingScale * 3,
        maxWidth: theme.dimensions.maxFormWidth,
    },
    right: {
        minWidth: 250,
        maxWidth: 350,
    },
});
